import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import StatCard from '../../components/docs/StatCard';
import FlowDiagram from '../../components/docs/FlowDiagram';
import ModeCard from '../../components/docs/ModeCard';
import CodeBlock from '../../components/docs/CodeBlock';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'trading-loop', label: 'The Trading Loop' },
  { id: 'text-vs-vision', label: 'Text vs Vision Mode' },
  { id: 'decision-schema', label: 'Decision Schema' },
  { id: 'fair-competition', label: 'Fair Competition' },
];

const tradingLoopSteps = [
  { number: '1', label: 'Market Data', sublabel: 'Candles & Context' },
  { number: '2', label: 'LLM Analysis', sublabel: 'Text or Vision Mode' },
  { number: '3', label: 'Decision', sublabel: 'LONG / SHORT / HOLD' },
  { number: '4', label: 'Execution', sublabel: 'Position Update' },
];

const decisionSchemaCode = `{
  "action": "LONG" | "SHORT" | "HOLD",
  "instrument": "DAX" | "NAS",
  "confidence": 0.0 - 1.0,
  "risk_percent": 1 - 5,
  "reasoning": "Analysis summary...",
  "stop_loss": 25200.0,
  "take_profit": 25400.0
}`;

const DocsOverview = () => {
  const seoConfig = SEO_CONFIG['/docs'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="trading-loop">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        How AI Models Trade
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        DayTradingBench is an autonomous trading benchmark that evaluates LLM decision-making
        capabilities in real-time market conditions. Each model receives live market data and
        makes independent trading decisions.
      </p>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
        <StatCard label="Active Models" value="13" accent="green" />
        <StatCard label="Markets" value="DAX, NAS" accent="blue" />
        <StatCard label="Decision Interval" value="15 min" accent="yellow" />
      </div>

      {/* Section 1: The Trading Loop */}
      <section id="trading-loop" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          The Trading Loop
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Every 15 minutes, each AI model receives a market snapshot with candle data and
          must decide whether to open a position or hold. Positions close automatically when
          price hits Stop Loss, Take Profit, or markets close.
        </p>
        <FlowDiagram steps={tradingLoopSteps} />
      </section>

      {/* Section 2: Text vs Vision Mode */}
      <section id="text-vs-vision" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Text vs Vision Mode
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ModeCard
            mode="TEXT"
            color="green"
            description="Models receive structured numerical data including OHLCV candles and market context in text format."
            models={['Gemini 3 Flash', 'Claude Haiku 4.5', 'DeepSeek V3.2', 'GLM-4.7']}
          />
          <ModeCard
            mode="VISION"
            color="purple"
            description="Models receive candlestick chart images, analyzing visual price patterns for trading decisions."
            models={['Claude Opus 4.6', 'Grok 4.1', 'Gemini 3 Pro', 'Claude Opus 4.5', 'Grok 4', 'GPT-5.2', 'GPT-5 Mini', 'GLM-4.6V', 'Kimi K2.5']}
          />
        </div>
      </section>

      {/* Section 3: Decision Schema */}
      <section id="decision-schema" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Decision Schema
        </h2>
        <CodeBlock
          filename="trading_decision.json"
          code={decisionSchemaCode}
        />
      </section>

      {/* Callout */}
      <section id="fair-competition" className="mb-12 scroll-mt-20">
        <Callout type="info" title="Fair Competition">
          All models start with the same $100,000 virtual balance and receive the exact same
          prompt with identical market data. This ensures we measure model decision-making
          capability, not prompt engineering quality.
        </Callout>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <div /> {/* Empty spacer for first page */}
        <Link
          to="/docs/how-it-works"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">How It Works</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default DocsOverview;
